import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Avatar, Box, Button, Stack, Typography } from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AssignmentIcon from "@mui/icons-material/Assignment";
import GroupIcon from "@mui/icons-material/Group";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { APP_ROUTES } from "../../../config/appRoutes";
import { useDispatch } from "react-redux";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ListIcon from "@mui/icons-material/List";
import { resetLeadManagementState } from "../leadMangementSlice";
import { QR } from "../../qrCode/components/qrUi";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";

// Header card for the lead pages: title, page tabs, optional stats / actions,
// and the page content inside the same card (same look as the other modules).
export default function Tabs({ stats = [], actions, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activePath, setActivePath] = useState(location.pathname);
  const dispatch = useDispatch();

  // ---------- Buttons ----------
  const leadButtons = [
    {
      label: "My Task",
      path: APP_ROUTES.LEADMANAGEMENT.MY_TASK,
      icon: <AssignmentIcon />,
    },
    {
      label: "My Team Task",
      path: APP_ROUTES.LEADMANAGEMENT.MY_TEAM_TASK,
      icon: <GroupIcon />,
    },
    {
      label: "Lead Management",
      path: APP_ROUTES.LEADMANAGEMENT.LEADMANAGEMENT,
      icon: <DashboardIcon />,
    },
    {
      label: "User Management",
      path: APP_ROUTES.LEADMANAGEMENT.USER_LEADMANAGEMENT,
      icon: <ManageAccountsIcon />,
    }];

  const tagButtons = [
    {
      label: "Tag List",
      path: APP_ROUTES.TAGS.TAGSLIST,
      icon: <ListIcon />,
    },
    {
      label: "Tag Creation",
      path: APP_ROUTES.TAGS.TAGCREATION,
      icon: <PersonAddIcon />,
    },
  ];

  // ---------- Track active path ----------
  useEffect(() => {
    setActivePath(location.pathname);
  }, [location.pathname]);

  const isActive = (path, isParent = false) =>
    isParent ? activePath.startsWith("/leadmanagement/tag") : activePath === path;

  // ---------- Underline tab style ----------
  const activeGlow = "linear-gradient(180deg, rgba(25,135,84,0) 0%, rgba(25,135,84,0.08) 100%)";
  const tabSx = (active) => ({
    position: "relative",
    flexShrink: 0,
    height: 52,
    px: { xs: 1.25, sm: 1.75 },
    gap: 1.1,
    borderRadius: "12px 12px 0 0",
    textTransform: "none",
    fontSize: 14.5,
    fontWeight: active ? 800 : 600,
    whiteSpace: "nowrap",
    color: active ? QR.dark : "#64748b",
    // soft green glow rising from the underline
    background: active ? activeGlow : "transparent",
    transition: "all .2s ease",
    "& .tab-ico": {
      width: 30,
      height: 30,
      borderRadius: 2.25,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: active ? "#fff" : "#94a3b8",
      background: active ? `linear-gradient(135deg, ${QR.main}, ${QR.light})` : "#f1f5f9",
      boxShadow: active ? `0 6px 12px -6px ${QR.main}` : "none",
      transition: "all .2s ease",
      "& svg": { fontSize: 17 },
    },
    // green bar under the active tab (gradient + glow)
    "&::after": {
      content: "\"\"",
      position: "absolute",
      left: 12,
      right: 12,
      bottom: -1,
      height: 3,
      borderRadius: "3px 3px 0 0",
      background: active ? `linear-gradient(90deg, ${QR.main}, ${QR.light})` : "transparent",
      boxShadow: active ? `0 -2px 8px ${QR.main}66` : "none",
      transition: "all .2s ease",
    },
    "&:hover": {
      background: active ? activeGlow : "#f8fafc",
      color: active ? QR.dark : "#0f172a",
      "& .tab-ico": active ? {} : { color: QR.main, bgcolor: `${QR.main}14` },
    },
  });

  const go = (path) => { navigate(path); dispatch(resetLeadManagementState()) };

  return (
    <Box
      sx={{
        position: "relative",
        p: { xs: 1.5, sm: 2, md: 2.5 },
        borderRadius: 3,
        border: `1px solid ${QR.border}`,
        bgcolor: "#fff",
        boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
      }}
    >
      {/* Title + optional page actions */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            variant="rounded"
            sx={{
              width: { xs: 40, sm: 46 },
              height: { xs: 40, sm: 46 },
              borderRadius: 2.5,
              color: "#fff",
              background: `linear-gradient(135deg, ${QR.main}, ${QR.light})`,
              boxShadow: `0 8px 18px -8px ${QR.main}`,
            }}
          >
            <PeopleAltIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} color="#0f172a" lineHeight={1.2} sx={{ fontSize: { xs: 17, sm: 20 } }}>
              Lead Management
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
              Track follow-ups, assign leads and manage your team
            </Typography>
          </Box>
        </Stack>
        {actions && (
          <Box sx={{ width: { xs: "100%", sm: "auto" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}>
            {actions}
          </Box>
        )}
      </Box>

      {/* Page tabs (underline) */}
      <Box
        sx={{
          mt: 2,
          // full-width strip across the card
          mx: { xs: -1.5, sm: -2, md: -2.5 },
          px: { xs: 1, sm: 1.5, md: 2 },
          display: "flex",
          gap: 0.5,
          overflowX: "auto",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          borderTop: `1px solid ${QR.border}`,
          borderBottom: `1px solid ${QR.border}`,
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {leadButtons.map((btn) => (
          <Button
            key={btn.path}
            onClick={() => go(btn.path)}
            aria-current={isActive(btn.path, btn.label === "Tags") ? "page" : undefined}
            sx={tabSx(isActive(btn.path, btn.label === "Tags"))}
          >
            <Box component="span" className="tab-ico">{btn.icon}</Box>
            {btn.label}
          </Button>
        ))}
      </Box>

      {/* Tag Buttons — visible only inside Tags routes */}
      {activePath.startsWith("/leadmanagement/tag") && (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
          {tagButtons.map((btn) => (
            <Button key={btn.path} onClick={() => go(btn.path)} sx={tabSx(isActive(btn.path))}>
              <Box component="span" className="tab-ico">{btn.icon}</Box>
              {btn.label}
            </Button>
          ))}
        </Stack>
      )}

      {/* Stats cards */}
      {stats.length > 0 && (
        <>
          {/* tell users these cards are choices */}
          {stats.some((s) => typeof s.onClick === "function") && (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 2 }}>
              <TouchAppOutlinedIcon sx={{ fontSize: 16, color: "#64748b" }} />
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#64748b" }}>
                Choose which leads to show
              </Typography>
            </Stack>
          )}
          <Box
            sx={{
              mt: stats.some((s) => typeof s.onClick === "function") ? 1 : 2,
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: `repeat(${stats.length}, minmax(0, 1fr))` },
              gap: 1.5,
            }}
          >
            {stats.map((s, idx) => {
              const clickable = typeof s.onClick === "function";
              return (
                <Stack
                  key={s.label}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  onClick={s.onClick}
                  {...(clickable ? {
                    role: "button",
                    tabIndex: 0,
                    "aria-pressed": !!s.active,
                    onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); s.onClick(); } },
                  } : {})}
                  sx={{
                    position: "relative",
                    gridColumn: { xs: stats.length % 2 === 1 && idx === stats.length - 1 ? "span 2" : "auto", md: "auto" },
                    minWidth: 0,
                    p: { xs: 1.25, sm: 1.5 },
                    pr: clickable ? { xs: 1.25, sm: 1.75 } : undefined,
                    borderRadius: 3,
                    cursor: clickable ? "pointer" : "default",
                    outline: "none",
                    bgcolor: s.active ? `${s.color}0d` : "#fff",
                    border: `${clickable ? "1.5px" : "1px"} solid ${s.active ? s.color : QR.border}`,
                    borderTop: `3px solid ${s.color}`,
                    boxShadow: s.active ? `0 6px 16px -10px ${s.color}` : "0 1px 3px rgba(16,24,40,0.06)",
                    transition: "all .15s ease",
                    "&:hover": clickable ? {
                      transform: "translateY(-2px)",
                      borderColor: s.color,
                      boxShadow: `0 10px 20px -12px ${s.color}`,
                      "& .stat-go": { color: s.color, bgcolor: `${s.color}14` },
                    } : {},
                    "&:focus-visible": clickable ? { boxShadow: `0 0 0 3px ${s.color}40` } : {},
                  }}
                >
                  <Avatar variant="rounded" sx={{ width: 40, height: 40, borderRadius: 2, color: s.color, bgcolor: `${s.color}14` }}>
                    {s.icon}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap display="block">
                      {s.label}
                    </Typography>
                    <Typography variant="h6" fontWeight={800} lineHeight={1.2} color="#0f172a">
                      {s.value}
                    </Typography>
                  </Box>

                  {/* choice indicator: selected = ✔ + "Showing", others = "View →" */}
                  {clickable && (s.active ? (
                    <Stack alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
                      <Box sx={{ width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", bgcolor: s.color, boxShadow: `0 4px 10px -4px ${s.color}` }}>
                        <CheckRoundedIcon sx={{ fontSize: 15 }} />
                      </Box>
                      <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: s.color, display: { xs: "none", sm: "block" } }}>Showing</Typography>
                    </Stack>
                  ) : (
                    <Stack
                      className="stat-go"
                      direction="row"
                      alignItems="center"
                      spacing={0.25}
                      sx={{ flexShrink: 0, px: 0.9, py: 0.4, borderRadius: 5, color: "#94a3b8", bgcolor: "#f1f5f9", transition: "all .15s ease" }}
                    >
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700, display: { xs: "none", sm: "block" } }}>View</Typography>
                      <ChevronRightRoundedIcon sx={{ fontSize: 17 }} />
                    </Stack>
                  ))}
                </Stack>
              );
            })}
          </Box>
        </>
      )}

      {/* Page content inside the same card */}
      {children && (
        <Box sx={{ mt: stats.length > 0 ? 2.5 : 1.5, pt: 0.5, borderTop: stats.length > 0 ? `1px solid ${QR.border}` : "none" }}>
          {children}
        </Box>
      )}
    </Box>
  );
}
